import type { ColumnDef } from '@tanstack/react-table'
import type { AccountLevel, IUser } from '@/shared/api/types/user-types'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { format, parseISO } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UserStatusToggle } from '@/features/user/user-status-toggle'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { DataTable } from '@/shared/ui/data-table'

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm')
  } catch {
    return '—'
  }
}

function initials(user: IUser): string {
  const source = (user.fullName ?? user.username ?? '?').trim()
  const parts = source.split(/\s+/).slice(0, 2)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?'
}

const ACCOUNT_LEVEL_VARIANT: Record<AccountLevel, 'default' | 'secondary' | 'outline'> = {
  SUPER_ADMIN: 'default',
  TENANT_ADMIN: 'secondary',
  USER: 'outline',
}

const ACCOUNT_LEVEL_LABEL_KEY: Record<AccountLevel, string> = {
  SUPER_ADMIN: 'user.accountLevelOptions.superAdmin',
  TENANT_ADMIN: 'user.accountLevelOptions.tenantAdmin',
  USER: 'user.accountLevelOptions.user',
}

interface UserListTableProps {
  users: IUser[]
  loading: boolean
  pagination: PaginationConfigInput
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (user: IUser) => void
}
export function UserListTable({
  users,
  loading,
  pagination,
  onPageChange,
  onEdit,
}: UserListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        id: 'user',
        header: t('user.title'),
        cell: ({ row }) => (
          <div className="flex min-w-[240px] items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={row.original.avatarUrl ?? undefined} alt={row.original.username} />
              <AvatarFallback>{initials(row.original)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-zinc-900">
                {row.original.fullName ?? row.original.username}
              </p>
              <p className="truncate text-xs text-zinc-500">@{row.original.username}</p>
            </div>
          </div>
        ),
      },
      {
        id: 'email',
        header: t('user.email'),
        cell: ({ row }) => row.original.email ?? <span className="text-zinc-400">—</span>,
      },
      {
        id: 'phone',
        header: t('user.phone'),
        cell: ({ row }) => row.original.phone ?? <span className="text-zinc-400">—</span>,
      },
      {
        id: 'accountLevel',
        header: t('user.accountLevel'),
        cell: ({ row }) => {
          const level = row.original.accountLevel
          if (!level) {
            return <span className="text-zinc-400">—</span>
          }
          return (
            <Badge variant={ACCOUNT_LEVEL_VARIANT[level]}>
              {t(ACCOUNT_LEVEL_LABEL_KEY[level])}
            </Badge>
          )
        },
      },
      {
        id: 'createdAt',
        header: t('user.createdAt'),
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'isActive',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <UserStatusToggle userId={row.original.id} isActive={row.original.isActive} />
        ),
      },
    ],
    [t],
  )

  return (
    <DataTable
      columns={columns}
      data={users}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: onPageChange,
      }}
      onRowClick={onEdit}
    />
  )
}
